import differenceWith from 'lodash/differenceWith';
import intersectionWith from 'lodash/intersectionWith';
import type {
  CoaContractBunkerInput,
  CoaContractInput,
  CoaContractPatch,
  CoaContractPortInput,
  CoaContractAssortmentInput,
  CoaContractPriceInput,
  CreateCoaPortPairMutation,
  CoaContractShipInput,
  PortDatumInput,
  CreateCoaAssortmentMutation,
  CommissionInput,
  CoaContractPortCostInput,
} from '../../graphql';
import {
  useCreateCoaMutation,
  useUpdateCoaMutation,
  useCreateCoaBunkerMutation,
  useUpdateCoaBunkerMutation,
  useDeleteCoaBunkerMutation,
  useCreateCoaPortPairMutation,
  useUpdateCoaPortPairMutation,
  useDeleteCoaPortPairMutation,
  useCreateCoaPortDataMutation,
  useCreateCoaAssortmentMutation,
  useUpdateCoaAssortmentMutation,
  useDeleteCoaAssortmentMutation,
  useCreateCoaPriceMutation,
  useUpdateCoaPriceMutation,
  useDeleteCoaPriceMutation,
  useCreateCoaVesselMutation,
  useUpdateCoaVesselMutation,
  useDeleteCoaVesselMutation,
  useCreatePortDataMutation,
  useCreateCommissionMutation,
  useDeleteCommissionMutation,
  useUpdateCommissionMutation,
  useCreateCoaCommissionMutation,
  useDeleteCoaCommissionMutation,
  useUpdateCoaCommissionMutation,
  useCreateCoaPortCostMutation,
  useUpdateCoaPortCostMutation,
  useDeleteCoaPortCostMutation,
} from '../../graphql';
import type { WithId, WithNullableId } from '../../lib/util';
import { assertDefined, hasId, isDefined } from '../../lib/util';
import type { ExecutionResult } from 'graphql';
import { useUpdatePortData } from './portData';

type DbIdOrTmpId = number | string;
type WithDbId<T> = Omit<T, 'id'> & { id: number };
type WithDbIdOrTmpId<T> = Omit<T, 'id'> & { id: DbIdOrTmpId };

type CustomCommissionInput = Omit<
  CommissionInput,
  'organizationId' | 'coaRouteId' | 'coaAssortmentId'
> & {
  organization: {
    value: number;
    label: string | null;
  };
  coaCommissionId: number | null;
  coaRouteId: DbIdOrTmpId | null;
  coaAssortmentId: DbIdOrTmpId | null;
};

export type CreateCoaWithRelationsMutation = (
  coaContract: CoaContractInput,
  bunkers: Array<
    Omit<
      CoaContractBunkerInput,
      'coaContractId' | 'coaRouteId' | 'coaAssortmentId'
    > & {
      coaRouteId: DbIdOrTmpId | null;
      coaAssortmentId: DbIdOrTmpId | null;
    }
  >,
  portPairs: Array<
    WithDbIdOrTmpId<Omit<CoaContractPortInput, 'coaContractId'>>
  >,
  portData: Array<PortDatumInput>,
  assortments: Array<
    WithDbIdOrTmpId<Omit<CoaContractAssortmentInput, 'coaContractId'>>
  >,
  prices: Array<
    Omit<
      CoaContractPriceInput,
      'coaContractId' | 'routeId' | 'coaAssortmentId'
    > & {
      coaAssortmentId: DbIdOrTmpId;
      routeId: DbIdOrTmpId;
    }
  >,
  vessels: Array<Omit<CoaContractShipInput, 'coaContractId'>>,
  commission: Array<WithNullableId<CustomCommissionInput>>,
  portCosts: Array<
    Omit<WithNullableId<CoaContractPortCostInput>, 'coaContractId'>
  >,
) => Promise<number | undefined>;

const mapTmpPortPairIdsToCreatedIds = (
  portPairsToCreate: Array<
    WithDbIdOrTmpId<Omit<CoaContractPortInput, 'coaContractId'>>
  >,
  createPortPairsRes: ExecutionResult<CreateCoaPortPairMutation>[],
): Map<string, number> => {
  const dbIdByTmpId = new Map<string, number>();

  createPortPairsRes.forEach((res, index) => {
    const tmpId = portPairsToCreate[index].id;

    if (
      typeof tmpId === 'string' &&
      res.data?.createCoaContractPort?.coaContractPort
    ) {
      const apiId = res.data.createCoaContractPort.coaContractPort.id;

      dbIdByTmpId.set(tmpId, apiId);
    }
  });

  return dbIdByTmpId;
};

const mapTmpCoaAssortmentIdsToCreatedIds = (
  coaAssortmentsToCreate: Array<
    WithDbIdOrTmpId<Omit<CoaContractAssortmentInput, 'coaContractId'>>
  >,
  createCoaAssortmentRes: ExecutionResult<CreateCoaAssortmentMutation>[],
): Map<string, number> => {
  const dbIdByTmpId = new Map<string, number>();

  createCoaAssortmentRes.forEach((res, index) => {
    const tmpId = coaAssortmentsToCreate[index].id;

    if (
      typeof tmpId === 'string' &&
      res.data?.createCoaContractAssortment?.coaContractAssortment
    ) {
      const apiId =
        res.data.createCoaContractAssortment.coaContractAssortment.id;

      dbIdByTmpId.set(tmpId, apiId);
    }
  });

  return dbIdByTmpId;
};

export function useCreateCoaWithRelationsMutation(): [
  CreateCoaWithRelationsMutation,
  { loading: boolean },
] {
  const [createCoa, { loading: createCoaLoading }] = useCreateCoaMutation();
  const [createCoaBunker, { loading: createCoaBankerLoading }] =
    useCreateCoaBunkerMutation();
  const [createCoaPortPair, { loading: createCoaPortPairLoading }] =
    useCreateCoaPortPairMutation();
  const [createPortDataRelation, { loading: createPortDataRelationLoading }] =
    useCreateCoaPortDataMutation();
  const [createPortData, { loading: createPortDataLoading }] =
    useCreatePortDataMutation();
  const [createAssortment, { loading: createAssortmentLoading }] =
    useCreateCoaAssortmentMutation();
  const [createPrice, { loading: createPriceLoading }] =
    useCreateCoaPriceMutation();
  const [createVessel, { loading: createVesselLoading }] =
    useCreateCoaVesselMutation();
  const [updateCommissions] = useCoaCommissions();
  const [createPortCost, { loading: createPortCostLoading }] =
    useCreateCoaPortCostMutation();

  const mut: CreateCoaWithRelationsMutation = async (
    coaContract,
    bunkers,
    portPairs,
    portData,
    assortments,
    prices,
    vessels,
    commissions,
    portCosts,
  ) => {
    const createCoaRes = await createCoa({
      variables: { input: { coaContract } },
    });

    if (!createCoaRes.data) {
      return;
    }

    const coaId = createCoaRes.data.createCoaContract?.coaContract?.id;

    if (!coaId) {
      throw new Error(`Created coa id unavailable`);
    }

    const createPortPairsRes = await Promise.all(
      portPairs.map(({ id, ...portPair }) =>
        createCoaPortPair({
          variables: {
            input: {
              coaContractPort: {
                ...portPair,
                coaContractId: coaId,
              },
            },
          },
        }),
      ),
    );

    const createdPortPairIdByTmpId = mapTmpPortPairIdsToCreatedIds(
      portPairs,
      createPortPairsRes,
    );

    const createPortDataResponses = await Promise.all(
      portData.map(({ ...portDatum }) =>
        createPortData({
          variables: {
            input: {
              portDatum,
            },
          },
        }),
      ),
    );

    const createdPortDataIds = createPortDataResponses.map((res): number => {
      const createdId = res.data?.createPortDatum?.portDatum?.id;

      assertDefined(createdId, 'created portDatum id');

      return createdId;
    });

    await Promise.all(
      createdPortDataIds.map((portDataId) =>
        createPortDataRelation({
          variables: {
            input: {
              coaContractPortDatum: {
                portDataId,
                coaContractId: coaId,
              },
            },
          },
        }),
      ),
    );

    const createCoaAssortmentRes = await Promise.all(
      assortments.map(({ id, ...assortment }) =>
        createAssortment({
          variables: {
            input: {
              coaContractAssortment: {
                coaContractId: coaId,
                ...assortment,
                assortmentId: assortment.assortmentId
                  ? assortment.assortmentId
                  : null,
              },
            },
          },
        }),
      ),
    );

    const createdAssortmentIdByTmpId = mapTmpCoaAssortmentIdsToCreatedIds(
      assortments,
      createCoaAssortmentRes,
    );

    await Promise.all(
      bunkers.map(({ coaAssortmentId, coaRouteId, ...bunker }) =>
        createCoaBunker({
          variables: {
            input: {
              coaContractBunker: {
                ...bunker,
                coaContractId: coaId,
                coaAssortmentId:
                  typeof coaAssortmentId === 'string'
                    ? createdAssortmentIdByTmpId.get(coaAssortmentId)
                    : coaAssortmentId,
                coaRouteId:
                  typeof coaRouteId === 'string'
                    ? createdPortPairIdByTmpId.get(coaRouteId)
                    : coaRouteId,
              },
            },
          },
        }),
      ),
    );

    await Promise.all(
      prices.map(({ routeId, coaAssortmentId, ...price }) =>
        createPrice({
          variables: {
            input: {
              coaContractPrice: {
                coaContractId: coaId,
                ...price,
                routeId:
                  typeof routeId === 'string'
                    ? createdPortPairIdByTmpId.get(routeId)
                    : routeId,
                coaAssortmentId:
                  typeof coaAssortmentId === 'string'
                    ? (createdAssortmentIdByTmpId.get(coaAssortmentId) ?? NaN)
                    : coaAssortmentId,
              },
            },
          },
        }),
      ),
    );

    await Promise.all(
      vessels.map((vessel) =>
        createVessel({
          variables: {
            input: {
              coaContractShip: {
                coaContractId: coaId,
                ...vessel,
              },
            },
          },
        }),
      ),
    );

    await Promise.all(
      portCosts.map(({ id, ...portCost }) =>
        createPortCost({
          variables: {
            input: {
              coaContractPortCost: {
                ...portCost,
                coaContractId: coaId,
              },
            },
          },
        }),
      ),
    );

    await updateCommissions(
      coaId,
      commissions,
      [],
      createdAssortmentIdByTmpId,
      createdPortPairIdByTmpId,
    );
    return coaId;
  };

  return [
    mut,
    {
      loading:
        createCoaLoading ||
        createCoaBankerLoading ||
        createCoaPortPairLoading ||
        createPortDataRelationLoading ||
        createPortDataLoading ||
        createAssortmentLoading ||
        createPriceLoading ||
        createVesselLoading ||
        createPortCostLoading,
    },
  ];
}

export type NotNullDataPrice = {
  routeId: string | number;
  coaAssortmentId: string | number;
  id: number | null;
  price: number;
  shipId: number | null;
};

export type UpdateCoaWithRelationsMutation = (
  coaContractPatch: WithId<CoaContractPatch>,
  newBunkers: Array<
    WithNullableId<
      Omit<
        CoaContractBunkerInput,
        'coaContractId' | 'coaRouteId' | 'coaAssortmentId'
      >
    > & {
      coaRouteId: DbIdOrTmpId | null;
      coaAssortmentId: DbIdOrTmpId | null;
    }
  >,
  oldBunkers: Array<WithId<CoaContractBunkerInput>>,
  newPortPairs: Array<
    WithDbIdOrTmpId<Omit<CoaContractPortInput, 'coaContractId'>>
  >,
  oldPortPairs: Array<Omit<WithId<CoaContractPortInput>, 'coaContractId'>>,
  newPortData: Array<WithNullableId<PortDatumInput>>,
  oldPortData: Array<WithId<PortDatumInput>>,
  newAssortments: Array<
    WithDbIdOrTmpId<Omit<CoaContractAssortmentInput, 'coaContractId'>>
  >,
  oldAssortments: Array<WithId<CoaContractAssortmentInput>>,
  newPrices: Array<
    WithNullableId<
      Omit<
        CoaContractPriceInput,
        'coaContractId' | 'routeId' | 'coaAssortmentId'
      >
    > & {
      coaAssortmentId: DbIdOrTmpId;
      routeId: DbIdOrTmpId;
    }
  >,
  oldPrices: Array<WithId<CoaContractPriceInput>>,
  newVessels: Array<
    Omit<WithNullableId<CoaContractShipInput>, 'coaContractId'>
  >,
  oldVessels: Array<Omit<WithId<CoaContractShipInput>, 'coaContractId'>>,
  newCommissions: Array<
    Omit<WithNullableId<CustomCommissionInput>, 'coaContractId'>
  >,
  oldCommissions: Array<
    WithId<CustomCommissionInput> & {
      coaCommissionId: number;
      coaRouteId: number | null | string;
      coaAssortmentId: number | null | string;
    }
  >,
  newPortCosts: Array<
    Omit<WithNullableId<CoaContractPortCostInput>, 'coaContractId'>
  >,
  oldPortCosts: Array<WithId<CoaContractPortCostInput>>,
) => Promise<void>;

const hasSameId = <
  T1 extends Record<string, unknown>,
  T2 extends Record<string, unknown>,
>(
  a: WithNullableId<T1>,
  b: WithNullableId<T2>,
) => hasId(a) && hasId(b) && a.id === b.id;

const isUpdatedBunker = (
  a: Partial<
    WithNullableId<
      Omit<
        CoaContractBunkerInput,
        'coaContractId' | 'coaRouteId' | 'coaAssortmentId'
      > & {
        coaRouteId: DbIdOrTmpId | null;
        coaAssortmentId: DbIdOrTmpId | null;
      }
    >
  >,
  b: Partial<WithId<CoaContractBunkerInput>>,
): boolean =>
  a.id === b.id &&
  (a.bunkerGrade !== b.bunkerGrade ||
    `${a.price}` !== `${b.price}` ||
    `${a.basePriceMax}` !== `${b.basePriceMax}` ||
    `${a.coaAssortmentId}` !== `${b.coaAssortmentId}` ||
    `${a.coaRouteId}` !== `${b.coaRouteId}` ||
    `${a.bafType}` !== `${b.bafType}` ||
    `${a.bafStepEffect}` !== `${b.bafStepEffect}` ||
    `${a.bafStepSize}` !== `${b.bafStepSize}` ||
    `${a.bafStartFromBase}` !== `${b.bafStartFromBase}` ||
    `${a.bafUseStepEffectAsValue}` !== `${b.bafUseStepEffectAsValue}` ||
    `${a.bafBunkerAmount}` !== `${b.bafBunkerAmount}`);

const isUpdatedPortPair = (
  a: WithDbIdOrTmpId<Partial<CoaContractPortInput>>,
  b: WithId<Partial<CoaContractPortInput>>,
): boolean =>
  a.id === b.id &&
  (a.originId !== b.originId || a.destinationId !== b.destinationId);

const isUpdatedAssortment = (
  a: Partial<
    WithDbIdOrTmpId<Omit<CoaContractAssortmentInput, 'coaContractId'>>
  >,
  b: Partial<WithId<CoaContractAssortmentInput>>,
) =>
  a.id === b.id &&
  (a.assortmentId !== b.assortmentId ||
    a.paymentUnit !== b.paymentUnit ||
    `${a.minAmount}` !== `${b.minAmount}` ||
    `${a.maxAmount}` !== `${b.maxAmount}`);

const isUpdatedPrice = (
  a: Partial<
    WithNullableId<
      Omit<
        CoaContractPriceInput,
        'coaContractId' | 'routeId' | 'coaAssortmentId'
      > & {
        coaAssortmentId: DbIdOrTmpId;
        routeId: DbIdOrTmpId;
      }
    >
  >,
  b: Partial<WithId<CoaContractPriceInput>>,
) =>
  a.id === b.id &&
  (a.coaAssortmentId !== b.coaAssortmentId ||
    a.price !== b.price ||
    a.routeId !== b.routeId ||
    a.shipId !== b.shipId);

const isUpdatedVessel = (
  a: Partial<WithNullableId<CoaContractShipInput>>,
  b: Partial<WithId<CoaContractShipInput>>,
) => a.id === b.id && (a.shipId !== b.shipId || a.demurrage !== b.demurrage);

const isUpdatedPortCost = (
  a: Partial<WithNullableId<CoaContractPortCostInput>>,
  b: WithId<Partial<CoaContractPortCostInput>>,
): boolean =>
  a.id === b.id &&
  (a.locationId !== b.locationId || a.shipId !== b.shipId || a.cost !== b.cost);

export function useUpdateCoaWithRelationsMutation(): [
  UpdateCoaWithRelationsMutation,
] {
  const [updateCoa] = useUpdateCoaMutation();

  const [createBunker] = useCreateCoaBunkerMutation();
  const [updateBunker] = useUpdateCoaBunkerMutation();
  const [deleteBunker] = useDeleteCoaBunkerMutation();

  const [createPortPair] = useCreateCoaPortPairMutation();
  const [updatePortPair] = useUpdateCoaPortPairMutation();
  const [deletePortPair] = useDeleteCoaPortPairMutation();

  const [createPortDataRelation] = useCreateCoaPortDataMutation();
  const [updatePortData] = useUpdatePortData();

  const [createAssortment] = useCreateCoaAssortmentMutation();
  const [updateAssortment] = useUpdateCoaAssortmentMutation();
  const [deleteAssortment] = useDeleteCoaAssortmentMutation();

  const [createPrice] = useCreateCoaPriceMutation();
  const [updatePrice] = useUpdateCoaPriceMutation();
  const [deletePrice] = useDeleteCoaPriceMutation();

  const [createVessel] = useCreateCoaVesselMutation();
  const [updateVessel] = useUpdateCoaVesselMutation();
  const [deleteVessel] = useDeleteCoaVesselMutation();

  const [createPortCost] = useCreateCoaPortCostMutation();
  const [updatePortCost] = useUpdateCoaPortCostMutation();
  const [deletePortCost] = useDeleteCoaPortCostMutation();

  const [updateCommissions] = useCoaCommissions();

  const mut: UpdateCoaWithRelationsMutation = async (
    { id: coaId, ...coaContractPatch },
    newBunkers,
    oldBunkers,
    newPortPairs,
    oldPortPairs,
    newPortData,
    oldPortData,
    newAssortments,
    oldAssortments,
    newPrices,
    oldPrices,
    newVessels,
    oldVessels,
    newCommissions,
    oldCommissions,
    newPortCosts,
    oldPortCosts,
  ) => {
    await updateCoa({
      variables: { input: { id: coaId, coaContractPatch } },
    });

    const bunkersToDelete = differenceWith(oldBunkers, newBunkers, hasSameId);
    const bunkersToUpdate = intersectionWith(
      newBunkers,
      oldBunkers,
      isUpdatedBunker,
    ).filter(hasId);
    const bunkersToCreate = differenceWith(newBunkers, oldBunkers, hasSameId);

    const portPairsToDelete = differenceWith(
      oldPortPairs,
      newPortPairs,
      hasSameId,
    );

    const portPairsToUpdate = intersectionWith(
      newPortPairs,
      oldPortPairs,
      isUpdatedPortPair,
    ).filter(
      (pair): pair is WithDbId<typeof pair> => typeof pair.id === 'number',
    );

    const portPairsToCreate = differenceWith(
      newPortPairs,
      oldPortPairs,
      hasSameId,
    );

    const assortmentsToDelete = differenceWith(
      oldAssortments,
      newAssortments,
      hasSameId,
    );

    const assortmentsToUpdate = intersectionWith(
      newAssortments,
      oldAssortments,
      isUpdatedAssortment,
    ).filter(
      (
        assortment,
      ): assortment is Omit<typeof assortment, 'id'> & {
        id: number;
      } => typeof assortment.id === 'number',
    );

    const assortmentsToCreate = differenceWith(
      newAssortments,
      oldAssortments,
      hasSameId,
    );

    const pricesToDelete = differenceWith(oldPrices, newPrices, hasSameId);
    const pricesToUpdate = intersectionWith(
      newPrices,
      oldPrices,
      isUpdatedPrice,
    ).filter(
      (
        price,
      ): price is Omit<typeof price, 'id'> & {
        id: number;
      } => typeof price.id === 'number',
    );

    const pricesToCreate = differenceWith(newPrices, oldPrices, hasSameId);

    const vesselsToDelete = differenceWith(oldVessels, newVessels, hasSameId);
    const vesselsToUpdate = intersectionWith(
      newVessels,
      oldVessels,
      isUpdatedVessel,
    ).filter(hasId);
    const vesselsToCreate = differenceWith(newVessels, oldVessels, hasSameId);

    const portCostsToDelete = differenceWith(
      oldPortCosts,
      newPortCosts,
      hasSameId,
    );

    const portCostsToUpdate = intersectionWith(
      newPortCosts,
      oldPortCosts,
      isUpdatedPortCost,
    ).filter(
      (pair): pair is WithDbId<typeof pair> => typeof pair.id === 'number',
    );

    const portCostsToCreate = differenceWith(
      newPortCosts,
      oldPortCosts,
      hasSameId,
    );

    await Promise.all(
      portPairsToUpdate.map(({ id, ...patch }) =>
        updatePortPair({
          variables: { input: { id, coaContractPortPatch: patch } },
        }),
      ),
    );

    const createPortPairsRes = await Promise.all(
      portPairsToCreate.map(({ id, ...portPair }) =>
        createPortPair({
          variables: {
            input: { coaContractPort: { coaContractId: coaId, ...portPair } },
          },
        }),
      ),
    );

    const portPairTmpIdMap = mapTmpPortPairIdsToCreatedIds(
      portPairsToCreate,
      createPortPairsRes,
    );

    const { createPortDataResponses } = await updatePortData(
      newPortData,
      oldPortData,
    );

    const createdPortDataIds = createPortDataResponses.map((res): number => {
      const createdId = res.data?.createPortDatum?.portDatum?.id;

      assertDefined(createdId, 'created portDatum id');

      return createdId;
    });

    await Promise.all(
      createdPortDataIds.map((portDataId) =>
        createPortDataRelation({
          variables: {
            input: {
              coaContractPortDatum: {
                portDataId,
                coaContractId: coaId,
              },
            },
          },
        }),
      ),
    );

    await Promise.all(
      pricesToDelete.map((price) =>
        deletePrice({ variables: { input: { id: price.id } } }),
      ),
    );

    await Promise.all(
      portPairsToDelete.map((portPair) =>
        deletePortPair({ variables: { input: { id: portPair.id } } }),
      ),
    );

    await Promise.all(
      assortmentsToDelete.map((assortment) =>
        deleteAssortment({ variables: { input: { id: assortment.id } } }),
      ),
    );

    await Promise.all(
      assortmentsToUpdate.map(({ id, ...patch }) =>
        updateAssortment({
          variables: {
            input: {
              id,
              coaContractAssortmentPatch: {
                ...patch,
                ...(patch.hasOwnProperty('assortmentId') && {
                  assortmentId: patch.assortmentId || null,
                }),
              },
            },
          },
        }),
      ),
    );

    await Promise.all(
      bunkersToDelete.map((bunker) =>
        deleteBunker({ variables: { input: { id: bunker.id } } }),
      ),
    );

    await Promise.all(
      vesselsToDelete.map((vessel) =>
        deleteVessel({ variables: { input: { id: vessel.id } } }),
      ),
    );

    await Promise.all(
      vesselsToUpdate.map(({ id, ...patch }) =>
        updateVessel({
          variables: { input: { id, coaContractShipPatch: patch } },
        }),
      ),
    );

    await Promise.all(
      vesselsToCreate.map(({ id, ...vessel }) =>
        createVessel({
          variables: {
            input: { coaContractShip: { coaContractId: coaId, ...vessel } },
          },
        }),
      ),
    );

    await Promise.all(
      portCostsToDelete.map((portCost) =>
        deletePortCost({
          variables: { input: { id: portCost.id } },
        }),
      ),
    );

    await Promise.all(
      portCostsToUpdate.map(({ id, ...patch }) =>
        updatePortCost({
          variables: {
            input: { id, coaContractPortCostPatch: patch },
          },
        }),
      ),
    );

    await Promise.all(
      portCostsToCreate.map(({ id, ...portCost }) =>
        createPortCost({
          variables: {
            input: {
              coaContractPortCost: {
                coaContractId: coaId,
                ...portCost,
              },
            },
          },
        }),
      ),
    );

    const createCoaAssortmentRes = await Promise.all(
      assortmentsToCreate.map(({ id, ...assortment }) =>
        createAssortment({
          variables: {
            input: {
              coaContractAssortment: {
                coaContractId: coaId,
                ...assortment,
                assortmentId: assortment.assortmentId
                  ? assortment.assortmentId
                  : null,
              },
            },
          },
        }),
      ),
    );

    const createdAssortmentIdByTmpId = mapTmpCoaAssortmentIdsToCreatedIds(
      assortmentsToCreate,
      createCoaAssortmentRes,
    );

    await Promise.all(
      bunkersToUpdate.map(({ id, coaAssortmentId, coaRouteId, ...patch }) =>
        updateBunker({
          variables: {
            input: {
              id,
              coaContractBunkerPatch: {
                ...patch,
                coaAssortmentId:
                  typeof coaAssortmentId === 'string'
                    ? createdAssortmentIdByTmpId.get(coaAssortmentId)
                    : coaAssortmentId,
                coaRouteId:
                  typeof coaRouteId === 'string'
                    ? portPairTmpIdMap.get(coaRouteId)
                    : coaRouteId,
              },
            },
          },
        }),
      ),
    );

    await Promise.all(
      bunkersToCreate.map(({ id, coaAssortmentId, coaRouteId, ...bunker }) =>
        createBunker({
          variables: {
            input: {
              coaContractBunker: {
                ...bunker,
                coaContractId: coaId,
                coaAssortmentId:
                  typeof coaAssortmentId === 'string'
                    ? createdAssortmentIdByTmpId.get(coaAssortmentId)
                    : coaAssortmentId,
                coaRouteId:
                  typeof coaRouteId === 'string'
                    ? portPairTmpIdMap.get(coaRouteId)
                    : coaRouteId,
              },
            },
          },
        }),
      ),
    );

    await Promise.all(
      pricesToUpdate.map(({ id, coaAssortmentId, routeId, ...patch }) =>
        updatePrice({
          variables: {
            input: {
              id,
              coaContractPricePatch: {
                ...patch,
                coaAssortmentId:
                  typeof coaAssortmentId === 'string'
                    ? createdAssortmentIdByTmpId.get(coaAssortmentId)
                    : coaAssortmentId,
                routeId:
                  typeof routeId === 'string'
                    ? portPairTmpIdMap.get(routeId)
                    : routeId,
              },
            },
          },
        }),
      ),
    );

    await Promise.all(
      pricesToCreate.map(({ id, routeId, coaAssortmentId, ...price }) =>
        createPrice({
          variables: {
            input: {
              coaContractPrice: {
                ...price,
                coaContractId: coaId,
                coaAssortmentId:
                  typeof coaAssortmentId === 'string'
                    ? (createdAssortmentIdByTmpId.get(coaAssortmentId) ?? NaN)
                    : coaAssortmentId,
                routeId:
                  typeof routeId === 'string'
                    ? portPairTmpIdMap.get(routeId)
                    : routeId,
              },
            },
          },
        }),
      ),
    );

    await updateCommissions(
      coaId,
      newCommissions,
      oldCommissions,
      createdAssortmentIdByTmpId,
      portPairTmpIdMap,
    );
  };

  return [mut];
}

export function useCoaCommissions() {
  const [createCommission] = useCreateCommissionMutation();
  const [deleteCommission] = useDeleteCommissionMutation();
  const [updateCommission] = useUpdateCommissionMutation();
  const [createCoaCommission] = useCreateCoaCommissionMutation();
  const [deleteCoaCommission] = useDeleteCoaCommissionMutation();
  const [updateCoaCommission] = useUpdateCoaCommissionMutation();

  async function updateCommissions(
    coaId: number,
    newCommissions: Array<WithNullableId<CustomCommissionInput>>,
    oldCommissions: Array<
      WithId<CustomCommissionInput> & {
        coaCommissionId: number;
        coaRouteId: number | null | string;
        coaAssortmentId: number | null | string;
      }
    >,
    createdAssortmentIdByTmpId: Map<string, number>,
    createdPortPairIdByTmpId: Map<string, number>,
  ) {
    const createdCommissions = newCommissions.filter((cm) => cm.id === null);
    const deletedCommissions = oldCommissions.filter(
      (oldCm) => !newCommissions.find((newCm) => oldCm.id === newCm.id),
    );

    const updatedCommissions = newCommissions.filter(
      (newCm): newCm is WithId<CustomCommissionInput> =>
        !!oldCommissions.find(
          (oldCm) =>
            newCm.id === oldCm.id &&
            (newCm.type !== oldCm.type ||
              newCm.value !== oldCm.value ||
              newCm.organization.value !== oldCm.organization.value ||
              newCm.demurrageApplied !== oldCm.demurrageApplied ||
              newCm.bafApplied !== oldCm.bafApplied ||
              newCm.extraFreightApplied !== oldCm.extraFreightApplied),
        ),
    );

    const updatedCoaCommissions = newCommissions.filter(
      (newCm): newCm is WithId<CustomCommissionInput> =>
        !!oldCommissions.find(
          (oldCm) =>
            newCm.id === oldCm.id &&
            newCm.coaCommissionId &&
            oldCm.coaCommissionId &&
            newCm.coaCommissionId === oldCm.coaCommissionId &&
            (newCm.coaAssortmentId !== oldCm.coaAssortmentId ||
              newCm.coaRouteId !== oldCm.coaRouteId),
        ),
    );

    const commissionsRes = await Promise.all(
      createdCommissions.map(
        ({
          type,
          value,
          bafApplied,
          demurrageApplied,
          extraFreightApplied,
          organization,
        }) =>
          createCommission({
            variables: {
              input: {
                commission: {
                  type,
                  value,
                  bafApplied,
                  demurrageApplied,
                  extraFreightApplied,
                  organizationId: organization.value
                    ? organization.value
                    : null,
                },
              },
            },
          }),
      ),
    ).then((res) => res.map((i) => i.data));

    const createdCommissionId = commissionsRes
      .map((i) => i?.createCommission?.commission?.id)
      .filter(isDefined);

    await Promise.all(
      createdCommissionId.map((commissionId, idx) => {
        const coaAssortmentId = createdCommissions[idx].coaAssortmentId;
        const tempCoaRouteId = createdCommissions[idx].coaRouteId;
        console.log(
          'coaRoute: ',
          typeof tempCoaRouteId === 'string'
            ? createdPortPairIdByTmpId.get(tempCoaRouteId)
            : null,
        );
        const coaRouteId = createdCommissions[idx].coaRouteId;
        createCoaCommission({
          variables: {
            input: {
              coaContractCommission: {
                coaContractId: coaId,
                commissionId,
                coaAssortmentId:
                  typeof coaAssortmentId === 'string'
                    ? createdAssortmentIdByTmpId.get(coaAssortmentId)
                    : coaAssortmentId,
                coaRouteId:
                  typeof coaRouteId === 'string'
                    ? createdPortPairIdByTmpId.get(coaRouteId)
                    : coaRouteId,
              },
            },
          },
        });
      }),
    );

    await Promise.all(
      deletedCommissions.map((cm) => {
        deleteCoaCommission({
          variables: {
            input: {
              id: cm.coaCommissionId,
            },
          },
        });
      }),
    );

    await Promise.all(
      deletedCommissions.map((cm) => {
        deleteCommission({
          variables: {
            input: {
              id: cm.id,
            },
          },
        });
      }),
    );

    await Promise.all(
      updatedCommissions.map(
        ({
          id,
          type,
          value,
          bafApplied,
          demurrageApplied,
          extraFreightApplied,
          organization,
        }) =>
          updateCommission({
            variables: {
              input: {
                id,
                commissionPatch: {
                  type,
                  value,
                  organizationId: organization.value
                    ? organization.value
                    : null,
                  bafApplied,
                  demurrageApplied,
                  extraFreightApplied,
                },
              },
            },
          }),
      ),
    );

    await Promise.all(
      updatedCoaCommissions.map(
        ({ coaAssortmentId, coaRouteId, coaCommissionId }) => {
          updateCoaCommission({
            variables: {
              input: {
                id: coaCommissionId ?? 0,
                coaContractCommissionPatch: {
                  coaAssortmentId:
                    typeof coaAssortmentId === 'string'
                      ? createdAssortmentIdByTmpId.get(coaAssortmentId)
                      : coaAssortmentId,
                  coaRouteId:
                    typeof coaRouteId === 'string'
                      ? createdPortPairIdByTmpId.get(coaRouteId)
                      : coaRouteId,
                },
              },
            },
          });
        },
      ),
    );
  }
  return [updateCommissions];
}
